import React, { useCallback, useEffect, useRef, useState } from "react";
import { Heading1, Type, Image as ImageIcon, MousePointerClick, Minus, ArrowUp, ArrowDown, Trash2, Send, FlaskConical, Loader2, Upload, AlignCenter, AlignLeft, RefreshCw, Save } from "lucide-react";
import { toast } from "sonner";
import { api, resolveMediaUrl } from "@/lib/api";

const BLOCK_TYPES = [
  ["title", "Titre", Heading1], ["text", "Texte", Type], ["image", "Image", ImageIcon],
  ["button", "Bouton", MousePointerClick], ["divider", "Séparateur", Minus],
];
const DEFAULT_BLOCKS = [
  { type: "title", text: "Salut {prenom} 👋", align: "left" },
  { type: "text", text: "Écris ton message ici. Mets du **gras** avec deux étoiles.", align: "left" },
  { type: "button", text: "Ouvrir Price", url: "https://pricecovoit.fr", align: "left" },
];
const TARGETS = [
  ["list", "1 personne / liste d'e-mails"], ["clients", "Tous les passagers"], ["drivers", "Tous les conducteurs"],
  ["all", "Tout le monde"], ["zone", "Un département"],
];
const errMsg = (e) => {
  const d = e?.response?.data?.detail;
  return typeof d === "string" ? d : Array.isArray(d) ? d[0]?.msg : "Erreur";
};

export default function StudioMailTab() {
  const [subject, setSubject] = useState("");
  const [preheader, setPreheader] = useState("");
  const [blocks, setBlocks] = useState(DEFAULT_BLOCKS);
  const [target, setTarget] = useState({ type: "list", emails: "", zone_code: "", zone_role: "all" });
  const [html, setHtml] = useState("");
  const [count, setCount] = useState(null);
  const [busy, setBusy] = useState(false);
  const [history, setHistory] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [tplId, setTplId] = useState(null);

  const payload = { subject: subject || "(sans objet)", preheader, blocks };

  useEffect(() => {
    const t = setTimeout(() => {
      api.post("/admin/price/mail/preview", payload).then((r) => setHtml(r.data.html)).catch(() => {});
    }, 350);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [subject, preheader, blocks]);

  useEffect(() => {
    setCount(null);
    if (target.type === "zone" && !target.zone_code) return undefined;
    if (target.type === "list" && !target.emails.trim()) { setCount(0); return undefined; }
    const t = setTimeout(() => {
      api.post("/admin/price/mail/count", target).then((r) => setCount(r.data.count)).catch(() => setCount(0));
    }, 500);
    return () => clearTimeout(t);
  }, [target]);

  const loadTemplates = useCallback(() => api.get("/admin/price/mail/templates").then((r) => setTemplates(r.data.items || [])).catch(() => {}), []);
  useEffect(() => { loadTemplates(); }, [loadTemplates]);

  const saveTemplate = async (asNew) => {
    if (!subject.trim()) { toast.error("Ajoute un objet"); return; }
    const current = templates.find((t) => t.id === tplId);
    const name = (!asNew && current) ? current.name : window.prompt("Nom du modèle", subject);
    if (!name) return;
    const body = { ...payload, name, target };
    try {
      const r = (!asNew && current)
        ? await api.put(`/admin/price/mail/templates/${current.id}`, body)
        : await api.post("/admin/price/mail/templates", body);
      setTplId(r.data.item.id);
      toast.success(`Modèle « ${name} » enregistré`);
      loadTemplates();
    } catch (e) { toast.error(errMsg(e)); }
  };

  const loadTemplate = (t) => {
    setSubject(t.subject); setPreheader(t.preheader || ""); setBlocks(t.blocks);
    if (t.target) setTarget({ type: "list", emails: "", zone_code: "", zone_role: "all", ...t.target, emails: t.target.emails || "" });
    setTplId(t.id);
    toast(`Modèle « ${t.name} » chargé`);
  };

  const resendTemplate = async (t) => {
    if (!window.confirm(`Renvoyer « ${t.subject} » aux mêmes destinataires ?`)) return;
    try {
      const r = await api.post(`/admin/price/mail/templates/${t.id}/send`);
      toast.success(`Envoi lancé à ${r.data.recipients_count} personne(s)`);
      loadHistory(); loadTemplates();
    } catch (e) { toast.error(errMsg(e)); }
  };

  const deleteTemplate = async (t) => {
    if (!window.confirm(`Supprimer le modèle « ${t.name} » ?`)) return;
    await api.delete(`/admin/price/mail/templates/${t.id}`).catch(() => {});
    if (tplId === t.id) setTplId(null);
    loadTemplates();
  };

  const loadHistory = useCallback(() => api.get("/admin/price/mail/campaigns").then((r) => setHistory(r.data.items || [])).catch(() => {}), []);
  useEffect(() => { loadHistory(); }, [loadHistory]);
  useEffect(() => {
    if (!history.some((h) => h.status === "sending")) return undefined;
    const t = setInterval(loadHistory, 4000);
    return () => clearInterval(t);
  }, [history, loadHistory]);

  const send = async (test) => {
    if (!subject.trim()) { toast.error("Ajoute un objet"); return; }
    if (!test && !window.confirm(`Envoyer « ${subject} » à ${count} personne(s) ?`)) return;
    setBusy(true);
    try {
      const r = await api.post(`/admin/price/mail/send${test ? "?test=true" : ""}`, { ...payload, target: test ? null : target });
      toast.success(test ? "Test envoyé sur ton adresse" : `Envoi lancé à ${r.data.recipients_count} personne(s)`);
      loadHistory();
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };

  return (
    <div className="space-y-4" data-testid="studio-mail-tab">
      <div className="rounded-2xl bg-white p-3 text-[12px] font-bold text-foreground/70 street-border-thin">
        Expéditeur : <span className="font-black text-foreground">Price Covoit &lt;contact@pricecovoit.fr&gt;</span> · les réponses arrivent sur <span className="font-black text-foreground">contact@pricecovoit.fr</span>.
      </div>
      <TemplatesBar items={templates} activeId={tplId} onLoad={loadTemplate} onResend={resendTemplate} onDelete={deleteTemplate} />
      <div className="grid gap-5">
        <div className="space-y-3">
          <input value={subject} onChange={(e) => setSubject(e.target.value)} placeholder="Objet de l'e-mail ({prenom} possible)" maxLength={200}
            className="w-full rounded-xl bg-white px-3 py-2.5 text-[15px] font-black street-border-thin" data-testid="mail-subject" />
          <input value={preheader} onChange={(e) => setPreheader(e.target.value)} placeholder="Aperçu dans la boîte mail (optionnel)" maxLength={200}
            className="w-full rounded-xl bg-white px-3 py-2 text-[13px] font-bold street-border-thin" data-testid="mail-preheader" />
          <BlocksEditor blocks={blocks} setBlocks={setBlocks} />
          <TargetPicker target={target} setTarget={setTarget} count={count} />
          <div className="flex flex-wrap gap-2">
            <button onClick={() => saveTemplate(false)} className="street-press inline-flex items-center gap-1.5 rounded-xl bg-accent px-4 py-2.5 text-[13px] font-black street-border-thin" data-testid="mail-save-template">
              <Save className="h-4 w-4" /> {tplId ? "Mettre à jour le modèle" : "Enregistrer comme modèle"}
            </button>
            {tplId && (
              <button onClick={() => saveTemplate(true)} className="street-press inline-flex items-center gap-1.5 rounded-xl bg-white px-4 py-2.5 text-[13px] font-black street-border-thin" data-testid="mail-save-template-new">
                <Save className="h-4 w-4" /> Nouveau modèle
              </button>
            )}
            <button onClick={() => send(true)} disabled={busy} className="street-press inline-flex items-center gap-1.5 rounded-xl bg-white px-4 py-2.5 text-[13px] font-black street-border-thin" data-testid="mail-send-test">
              <FlaskConical className="h-4 w-4" /> M&apos;envoyer un test
            </button>
            <button onClick={() => send(false)} disabled={busy || !count} className="street-press inline-flex items-center gap-1.5 rounded-xl bg-primary px-4 py-2.5 text-[13px] font-black text-primary-foreground street-border-thin disabled:opacity-50" data-testid="mail-send">
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />} Envoyer{count ? ` à ${count} personne${count > 1 ? "s" : ""}` : ""}
            </button>
          </div>
        </div>
        <div>
          <div className="mb-2 text-[11px] font-black uppercase tracking-widest text-foreground/50">Aperçu en direct ({"{prenom}"} = Amine)</div>
          <iframe title="Aperçu e-mail" srcDoc={html} className="h-[720px] w-full rounded-2xl bg-white street-border-thin" data-testid="mail-preview" />
        </div>
      </div>
      <MailHistory items={history} onRefresh={loadHistory} />
    </div>
  );
}

function BlocksEditor({ blocks, setBlocks }) {
  const upd = (i, patch) => setBlocks((b) => b.map((x, j) => (j === i ? { ...x, ...patch } : x)));
  const move = (i, d) => setBlocks((b) => {
    const n = [...b]; const j = i + d;
    if (j < 0 || j >= n.length) return b;
    [n[i], n[j]] = [n[j], n[i]]; return n;
  });
  return (
    <div className="space-y-2" data-testid="mail-blocks">
      {blocks.map((b, i) => (
        <BlockRow key={i} i={i} b={b} last={i === blocks.length - 1} onChange={(p) => upd(i, p)} onMove={(d) => move(i, d)}
          onDelete={() => setBlocks((x) => (x.length > 1 ? x.filter((_, j) => j !== i) : x))} />
      ))}
      <div className="flex flex-wrap gap-1.5">
        {BLOCK_TYPES.map(([t, l, Icon]) => (
          <button key={t} onClick={() => setBlocks((x) => [...x, { type: t, text: t === "button" ? "Ouvrir Price" : "", url: t === "button" ? "https://pricecovoit.fr" : "", align: "left" }])}
            className="street-press inline-flex items-center gap-1 rounded-lg bg-secondary/50 px-2.5 py-1.5 text-[12px] font-black street-border-thin" data-testid={`mail-add-${t}`}>
            <Icon className="h-3.5 w-3.5" />+ {l}
          </button>
        ))}
      </div>
    </div>
  );
}

function BlockRow({ i, b, last, onChange, onMove, onDelete }) {
  const fileRef = useRef(null);
  const [up, setUp] = useState(false);
  const label = BLOCK_TYPES.find(([t]) => t === b.type)?.[1];
  const upload = async (f) => {
    if (!f) return;
    setUp(true);
    try {
      const fd = new FormData(); fd.append("file", f);
      const r = await api.post("/admin/upload-image", fd, { headers: { "Content-Type": "multipart/form-data" } });
      onChange({ url: resolveMediaUrl(r.data.url) });
    } catch (e) { toast.error(errMsg(e)); } finally { setUp(false); }
  };
  const input = "w-full rounded-lg bg-secondary/30 px-2.5 py-2 text-[13px] font-medium street-border-thin";
  return (
    <div className="rounded-2xl bg-white p-3 street-border-thin" data-testid={`mail-block-${i}`}>
      <div className="mb-2 flex items-center gap-1">
        <span className="flex-1 text-[11px] font-black uppercase tracking-widest text-foreground/50">{label}</span>
        {["title", "text", "button"].includes(b.type) && (
          <button onClick={() => onChange({ align: b.align === "center" ? "left" : "center" })} className="rounded p-1 hover:bg-secondary" title="Alignement" data-testid={`mail-block-align-${i}`}>
            {b.align === "center" ? <AlignCenter className="h-4 w-4" /> : <AlignLeft className="h-4 w-4" />}
          </button>
        )}
        <button onClick={() => onMove(-1)} disabled={i === 0} className="rounded p-1 hover:bg-secondary disabled:opacity-30"><ArrowUp className="h-4 w-4" /></button>
        <button onClick={() => onMove(1)} disabled={last} className="rounded p-1 hover:bg-secondary disabled:opacity-30"><ArrowDown className="h-4 w-4" /></button>
        <button onClick={onDelete} className="rounded p-1 text-red-600 hover:bg-red-50" data-testid={`mail-block-delete-${i}`}><Trash2 className="h-4 w-4" /></button>
      </div>
      {b.type === "title" && <input value={b.text} onChange={(e) => onChange({ text: e.target.value })} className={input} placeholder="Titre" data-testid={`mail-block-text-${i}`} />}
      {b.type === "text" && <textarea value={b.text} onChange={(e) => onChange({ text: e.target.value })} rows={4} className={input} placeholder="Texte… ({prenom}, **gras**)" data-testid={`mail-block-text-${i}`} />}
      {b.type === "button" && (
        <div className="grid gap-2 sm:grid-cols-2">
          <input value={b.text} onChange={(e) => onChange({ text: e.target.value })} className={input} placeholder="Texte du bouton" data-testid={`mail-block-text-${i}`} />
          <input value={b.url} onChange={(e) => onChange({ url: e.target.value })} className={input} placeholder="https://…" data-testid={`mail-block-url-${i}`} />
        </div>
      )}
      {b.type === "image" && (
        <div className="flex gap-2">
          <input value={b.url} onChange={(e) => onChange({ url: e.target.value })} className={input} placeholder="URL de l'image (https://…)" data-testid={`mail-block-url-${i}`} />
          <button onClick={() => fileRef.current?.click()} className="inline-flex flex-none items-center gap-1 rounded-lg bg-accent px-2.5 text-[12px] font-black street-border-thin" data-testid={`mail-block-upload-${i}`}>
            {up ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />} Importer
          </button>
          <input ref={fileRef} type="file" accept="image/png,image/jpeg,image/webp,image/gif" className="hidden" onChange={(e) => { upload(e.target.files?.[0]); e.target.value = ""; }} />
        </div>
      )}
      {b.type === "divider" && <div className="border-t-2 border-dashed border-black/20" />}
    </div>
  );
}

export function TargetPicker({ target, setTarget, count }) {
  const [zones, setZones] = useState([]);
  useEffect(() => {
    if (target.type !== "zone" || zones.length) return;
    Promise.all(["clients", "drivers"].map((r) => api.get(`/admin/price/users-by-city?role=${r}`)))
      .then((res) => {
        const m = {};
        res.forEach((r) => r.data.zones.forEach((z) => { if (z.code !== "ZZ") m[z.code] = z.label; }));
        setZones(Object.entries(m).sort());
      }).catch(() => {});
  }, [target.type, zones.length]);
  const set = (p) => setTarget((t) => ({ ...t, ...p }));
  return (
    <div className="space-y-2 rounded-2xl bg-white p-3 street-border-thin" data-testid="mail-target">
      <div className="flex items-center justify-between">
        <span className="text-[11px] font-black uppercase tracking-widest text-foreground/50">Destinataires</span>
        <span className="rounded-full bg-black px-2.5 py-0.5 text-[12px] font-black text-white" data-testid="mail-count">{count == null ? "…" : count}</span>
      </div>
      <select value={target.type} onChange={(e) => set({ type: e.target.value })} className="w-full rounded-lg px-2 py-2 text-[13px] font-bold street-border-thin" data-testid="mail-target-type">
        {TARGETS.map(([k, l]) => <option key={k} value={k}>{l}</option>)}
      </select>
      {target.type === "list" && (
        <textarea value={target.emails} onChange={(e) => set({ emails: e.target.value })} rows={3} placeholder="ex. jean@mail.com, sara@mail.com (une ou plusieurs adresses)"
          className="w-full rounded-lg bg-secondary/30 px-2.5 py-2 text-[13px] street-border-thin" data-testid="mail-target-emails" />
      )}
      {target.type === "zone" && (
        <div className="grid grid-cols-2 gap-2">
          <select value={target.zone_code} onChange={(e) => set({ zone_code: e.target.value })} className="rounded-lg px-2 py-2 text-[13px] font-bold street-border-thin" data-testid="mail-target-zone">
            <option value="">Département…</option>
            {zones.map(([c, l]) => <option key={c} value={c}>{l}</option>)}
          </select>
          <select value={target.zone_role} onChange={(e) => set({ zone_role: e.target.value })} className="rounded-lg px-2 py-2 text-[13px] font-bold street-border-thin" data-testid="mail-target-zone-role">
            <option value="all">Passagers + conducteurs</option>
            <option value="clients">Passagers</option>
            <option value="drivers">Conducteurs</option>
          </select>
        </div>
      )}
      <p className="text-[11px] text-foreground/50">Les personnes désinscrites (lien « Ne plus recevoir ») sont exclues automatiquement des envois groupés.</p>
    </div>
  );
}

const TARGET_LABEL = { list: "Liste", clients: "Passagers", drivers: "Conducteurs", all: "Tout le monde", zone: "Département", user: "1 personne" };

function TemplatesBar({ items, activeId, onLoad, onResend, onDelete }) {
  if (!items.length) return null;
  return (
    <div className="rounded-2xl bg-white p-3 street-border-thin" data-testid="mail-templates">
      <div className="mb-2 font-[Outfit] text-[15px] font-black">Mes modèles enregistrés</div>
      <div className="divide-y divide-black/5">
        {items.map((t) => (
          <div key={t.id} className={"flex flex-wrap items-center gap-2 py-2 " + (t.id === activeId ? "bg-primary/5" : "")} data-testid={`mail-template-${t.id}`}>
            <div className="min-w-0 flex-1">
              <div className="truncate text-[13px] font-black">{t.name}</div>
              <div className="truncate text-[11px] text-foreground/60">
                {t.subject} · {TARGET_LABEL[t.target?.type] || "—"}{t.target?.zone_code ? ` ${t.target.zone_code}` : ""}
                {t.last_sent_at ? ` · envoyé le ${new Date(t.last_sent_at).toLocaleDateString("fr-FR")}` : ""}
              </div>
            </div>
            <button onClick={() => onLoad(t)} className="rounded-lg bg-white px-2.5 py-1.5 text-[11px] font-black street-border-thin" data-testid={`mail-template-load-${t.id}`}>Modifier</button>
            <button onClick={() => onResend(t)} className="inline-flex items-center gap-1 rounded-lg bg-primary px-2.5 py-1.5 text-[11px] font-black text-primary-foreground street-border-thin" data-testid={`mail-template-resend-${t.id}`}>
              <Send className="h-3 w-3" /> Renvoyer
            </button>
            <button onClick={() => onDelete(t)} className="rounded-lg p-1.5 text-red-600 hover:bg-red-50" data-testid={`mail-template-delete-${t.id}`}><Trash2 className="h-4 w-4" /></button>
          </div>
        ))}
      </div>
    </div>
  );
}

const STATUS = { sending: ["En cours", "bg-yellow-100"], done: ["Envoyé", "bg-green-100"], partial: ["Partiel", "bg-orange-100"], failed: ["Échec", "bg-red-100"] };

function MailHistory({ items, onRefresh }) {
  return (
    <div className="rounded-2xl bg-white p-3 street-border-thin" data-testid="mail-history">
      <div className="mb-2 flex items-center justify-between">
        <span className="font-[Outfit] text-[15px] font-black">Historique des envois</span>
        <button onClick={onRefresh} className="rounded p-1 hover:bg-secondary" data-testid="mail-history-refresh"><RefreshCw className="h-4 w-4" /></button>
      </div>
      {!items.length && <div className="text-[13px] text-foreground/50">Aucun envoi pour l&apos;instant.</div>}
      <div className="divide-y divide-black/5">
        {items.map((h) => {
          const [l, c] = STATUS[h.status] || [h.status, "bg-secondary"];
          return (
            <div key={h.id} className="flex flex-wrap items-center gap-x-3 gap-y-1 py-2 text-[12px]" data-testid={`mail-history-${h.id}`}>
              <span className="w-32 text-foreground/60">{new Date(h.created_at).toLocaleString("fr-FR", { dateStyle: "short", timeStyle: "short" })}</span>
              <span className="min-w-0 flex-1 truncate font-black">{h.subject}</span>
              <span className="font-bold">{h.sent}/{h.recipients_count} envoyé{h.sent > 1 ? "s" : ""}{h.failed ? ` · ${h.failed} échec` : ""}</span>
              <span className={`rounded px-2 py-0.5 text-[10px] font-black uppercase ${c}`}>{l}</span>
            </div>
          );
        })}
      </div>
    </div>
  );
}
